import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import Select from 'react-select'
import { useTextSummary } from 'api/hooks/use-text-summary'
import { smSelectStyles } from './searchable-column-select'

const DATALESS_TYPES = ['text', 'header', 'page-break']
const MAX_SOURCES = 10
const MAX_INSTRUCTIONS = 1000

const sourceKey = (source) => `${source.kind}:${source.id}`

export const formatAsOf = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

// Picks the datasets and charts a text block summarizes, plus optional instructions, and writes the
// summary into the editor. The selection is saved on the block, so Regenerate reuses it.
const TextSummaryPanel = ({
  adapter,
  containerId,
  chartId,
  summaryConfig,
  dataSources = [],
  onGenerated,
  onSaveAndGenerate = null
}) => {
  // Open from the start so authors find it; the button collapses it for anyone writing by hand.
  const [isOpen, setIsOpen] = useState(!!chartId || !!onSaveAndGenerate)
  const [selected, setSelected] = useState(() => (summaryConfig?.sources || []).map(sourceKey))
  const [instructions, setInstructions] = useState(summaryConfig?.instructions || '')
  const [includeFigures, setIncludeFigures] = useState(!!summaryConfig?.include_figures)
  const [isSaving, setIsSaving] = useState(false)

  const { data: siblingCharts = [] } = adapter.useSiblingCharts({ containerId })
  const { generate, config, isGenerating, startError } = useTextSummary({
    adapter,
    containerId,
    chartId,
    summaryConfig,
    onFinished: (chart) => {
      if (chart?.params?.summary_config?.status === 'ready') onGenerated(chart.params.text_content || '')
    }
  })

  const groups = useMemo(() => {
    const data = adapter.summaryDataOptions({ containerId, dataSources })
    const charts = siblingCharts
      .filter((chart) => chart.id !== chartId && !DATALESS_TYPES.includes(chart.chart_type))
      .map((chart) => ({ kind: 'chart', id: chart.id, label: chart.title || 'Untitled chart' }))
    return [
      { label: 'Data', options: data },
      { label: 'Charts', options: charts }
    ]
  }, [adapter, containerId, dataSources, siblingCharts, chartId])

  const allOptions = groups.flatMap((group) => group.options)
  const value = allOptions.filter((option) => selected.includes(sourceKey(option)))
  const failure = startError || (config?.status === 'failed' ? config.error : null)
  const isBusy = isGenerating || isSaving
  const generateLabel = (isBusy && 'Writing summary…') || (chartId ? 'Generate summary' : 'Save and generate summary')

  const submit = () => {
    const selection = {
      sources: value.map(({ kind, id }) => ({ kind, id })),
      instructions,
      include_figures: includeFigures
    }
    if (chartId) return generate(selection)

    setIsSaving(true)
    return Promise.resolve(onSaveAndGenerate?.(selection)).finally(() => setIsSaving(false))
  }

  return (
    <div className="mt-3">
      <Button
        variant="light"
        className="text-primary"
        size="sm"
        disabled={!chartId && !onSaveAndGenerate}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
      >
        Summarize with AI
      </Button>

      {isOpen && (
        <div className="border rounded p-3 mt-2" data-testid="text-summary-panel">
          <Form.Group className="mb-3">
            <Form.Label htmlFor="text-summary-sources">Summarize</Form.Label>
            <Select
              inputId="text-summary-sources"
              isMulti
              options={groups}
              value={value}
              onChange={(options) => setSelected((options || []).map(sourceKey))}
              getOptionValue={sourceKey}
              isOptionDisabled={() => value.length >= MAX_SOURCES}
              placeholder="Choose data and charts..."
              noOptionsMessage={() => 'Nothing else to summarize'}
              styles={smSelectStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="text-summary-instructions">
            <Form.Label>Instructions (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              maxLength={MAX_INSTRUCTIONS}
              value={instructions}
              placeholder="e.g. Two sentences for the board, focused on what is excluded"
              onChange={(e) => setInstructions(e.target.value)}
            />
          </Form.Group>

          <Form.Check
            id="text-summary-include-figures"
            type="checkbox"
            className="mb-3"
            label="Include key figures"
            checked={includeFigures}
            onChange={(e) => setIncludeFigures(e.target.checked)}
          />

          {failure && (
            <p className="text-danger small" role="alert">
              The summary could not be written: {failure}
            </p>
          )}

          <div className="d-flex align-items-center gap-2">
            <Button size="sm" disabled={isBusy || value.length === 0} onClick={submit}>
              {generateLabel}
            </Button>
            <span className="text-muted small">
              {chartId ? 'Replaces the text above.' : 'Saves this block, then writes its text.'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

TextSummaryPanel.propTypes = {
  adapter: PropTypes.object.isRequired,
  containerId: PropTypes.string,
  chartId: PropTypes.string,
  summaryConfig: PropTypes.object,
  dataSources: PropTypes.array,
  onGenerated: PropTypes.func.isRequired,
  onSaveAndGenerate: PropTypes.func
}

// Under a summarized text block: when the summary was written, and a way to rewrite it from the
// saved selection for a new reporting period.
export const TextSummaryStatus = ({ adapter, containerId, chartId, summaryConfig, canEdit = false }) => {
  const { generate, config, isGenerating, startError } = useTextSummary({
    adapter,
    containerId,
    chartId,
    summaryConfig
  })

  if (!config?.sources?.length) return null

  const regenerate = canEdit && (
    <Button variant="link" size="sm" className="p-0 ms-2 align-baseline" disabled={isGenerating} onClick={() => generate()}>
      Regenerate
    </Button>
  )

  if (isGenerating) {
    return (
      <p className="text-muted mb-0 mt-2 small" data-testid="text-summary-status">
        <Spinner animation="border" size="sm" className="me-1" />
        Writing summary…
      </p>
    )
  }

  const failure = startError || (config.status === 'failed' ? config.error : null)

  return (
    <p className={`${failure ? 'text-danger' : 'text-muted'} mb-0 mt-2 small`} data-testid="text-summary-status">
      {failure
        ? `The summary could not be written: ${failure}`
        : config.generated_at && `Summary as of ${formatAsOf(config.generated_at)}`}
      {regenerate}
    </p>
  )
}

TextSummaryStatus.propTypes = {
  adapter: PropTypes.object.isRequired,
  containerId: PropTypes.string,
  chartId: PropTypes.string,
  summaryConfig: PropTypes.object,
  canEdit: PropTypes.bool
}

export default TextSummaryPanel
